/**
 * Chat instantané portail ↔ personnel administratif (2026-08-31).
 *
 * Réutilise l'API backoffice `/api/messagerie-chat/*` qui accepte déjà le
 * JWT portail étudiant via `PortailEtudiantAuthentication`.
 * Embarqué dans le hub `Messagerie.tsx` (onglet Personnel).
 * URL entrée : `/portail/messagerie?tab=personnel&conv=<id>`.
 *
 * 2026-10-03 : emojis, images (bouton ou Ctrl+V, aperçu avant envoi) et messages
 * vocaux, comme dans le chat des agents. Même point d'accès en multipart : le
 * serveur classe la pièce jointe (image, audio, fichier) et en vérifie la taille.
 */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Loader2, Send, MessageSquare, Briefcase, ArrowLeft, Image as ImageIcon, X,
} from 'lucide-react';
import { portailApi } from '../services/portailApi';
import { AudioRecorder } from '../components/AudioRecorder';
import { EmojiPicker } from '../components/EmojiPicker';
import PhotoLightbox from '../../components/ui/PhotoLightbox';

interface Conv {
  id: number;
  titre_affichage: string;
  avatar_url: string | null;
  date_derniere_activite: string;
  nb_non_lus: number;
  dernier_message: { contenu: string; date_envoi: string; expediteur_nom: string } | null;
  participants_apercu: Array<{ user_id: number; user_nom: string; role_conv: string }>;
}
interface Msg {
  id: number;
  expediteur_nom: string;
  expediteur_role: string;
  contenu: string;
  date_envoi: string;
  a_moi: boolean;
  type_msg: string;
  fichier_url?: string;
  fichier_nom?: string;
  fichier_mime?: string;
}

function fmtHeure(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('fr-FR', {
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return ''; }
}

function fmtDateCourt(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const memeJour = d.toDateString() === now.toDateString();
    if (memeJour) return fmtHeure(iso);
    return d.toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit',
    });
  } catch { return ''; }
}

const FORMATS_ACCEPTES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

/** Réduit une image trop lourde (> 1,5 Mo) ou d'un format refusé par le serveur
 *  (HEIC des iPhone…) en JPEG de 1600 px au plus. En cas d'échec, l'original. */
async function reduireImage(f: File): Promise<File> {
  if (f.size <= 1.5 * 1024 * 1024 && FORMATS_ACCEPTES.includes(f.type)) return f;
  try {
    const url = URL.createObjectURL(f);
    const img = await new Promise<HTMLImageElement>((ok, ko) => {
      const i = new Image();
      i.onload = () => ok(i);
      i.onerror = ko;
      i.src = url;
    });
    const ratio = Math.min(1, 1600 / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * ratio);
    canvas.height = Math.round(img.height * ratio);
    canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    const blob = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, 'image/jpeg', 0.82));
    if (!blob) return f;
    const nom = (f.name || 'image').replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], nom, { type: 'image/jpeg' });
  } catch {
    return f;
  }
}

const ROLE_BADGE: Record<string, string> = {
  agent: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  agent_caisse: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/40',
  formateur: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/40',
  super_admin: 'bg-rose-500/15 text-rose-300 border-rose-500/40',
  admin: 'bg-rose-500/15 text-rose-300 border-rose-500/40',
  directeur: 'bg-rose-500/15 text-rose-300 border-rose-500/40',
  surveillant: 'bg-orange-500/15 text-orange-300 border-orange-500/40',
};

interface MessagesPersonnelProps {
  embedded?: boolean;
}

export default function MessagesPersonnel({ embedded = false }: MessagesPersonnelProps = {}) {
  const [params, setParams] = useSearchParams();
  const convParam = params.get('conv');

  const [convs, setConvs] = useState<Conv[]>([]);
  const [loadingList, setLoadingList] = useState(true);

  const [selectedConvId, setSelectedConvId] = useState<number | null>(
    convParam ? Number(convParam) : null,
  );
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [loadingChat, setLoadingChat] = useState(false);

  const [texte, setTexte] = useState('');
  const [envoi, setEnvoi] = useState(false);
  // Pièces jointes (2026-10-03)
  const [showEmoji, setShowEmoji] = useState(false);
  const [audioPhase, setAudioPhase] = useState<'idle' | 'recording' | 'sending'>('idle');
  const [image, setImage] = useState<File | null>(null);
  const [apercu, setApercu] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{ url: string; nom?: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const chatEndRef = useRef<HTMLDivElement>(null);

  // Charger liste conv
  const chargerConvs = () => {
    setLoadingList(true);
    portailApi.personnelConversationsList()
      .then((r) => setConvs(r.results || []))
      .catch(() => setConvs([]))
      .finally(() => setLoadingList(false));
  };
  useEffect(() => { chargerConvs(); }, []);

  // Sync ?conv=X avec l'état
  useEffect(() => {
    const p = params.get('conv');
    if (p) setSelectedConvId(Number(p));
  }, [params]);

  // Charger messages quand conv sélectionnée
  const chargerMessages = (convId: number) => {
    setLoadingChat(true);
    portailApi.personnelMessagesList(convId)
      .then((r) => setMsgs(r.results || []))
      .catch(() => setMsgs([]))
      .finally(() => setLoadingChat(false));
    portailApi.personnelMarquerLus(convId).catch(() => {});
  };
  useEffect(() => {
    if (selectedConvId) chargerMessages(selectedConvId);
  }, [selectedConvId]);

  // Auto-scroll en bas au chargement de messages
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [msgs.length]);

  // Polling messages toutes les 8s tant que conv ouverte
  useEffect(() => {
    if (!selectedConvId) return;
    const iv = setInterval(() => {
      portailApi.personnelMessagesList(selectedConvId)
        .then((r) => setMsgs(r.results || []))
        .catch(() => {});
    }, 8000);
    return () => clearInterval(iv);
  }, [selectedConvId]);

  const selectionner = (c: Conv) => {
    setSelectedConvId(c.id);
    params.set('tab', 'personnel');
    params.set('conv', String(c.id));
    setParams(params, { replace: true });
  };

  const retourListe = () => {
    setSelectedConvId(null);
    setMsgs([]);
    params.delete('conv');
    setParams(params, { replace: true });
  };

  const choisirImage = async (f: File | null) => {
    if (apercu) URL.revokeObjectURL(apercu);
    if (f && !f.type.startsWith('image/')) {
      alert('Seules les images peuvent être jointes ici.');
      f = null;
    }
    // Le serveur limite les images à 5 Mo (JPEG, PNG, WebP, GIF) : une photo de
    // téléphone est réduite à 1600 px en JPEG avant l'envoi.
    if (f) f = await reduireImage(f);
    setImage(f);
    setApercu(f ? URL.createObjectURL(f) : null);
  };

  // Ctrl+V d'une image (capture d'écran…) pendant qu'une conversation est ouverte
  useEffect(() => {
    if (!selectedConvId) return;
    const onPaste = (e: ClipboardEvent) => {
      const item = Array.from(e.clipboardData?.items || []).find((i) => i.type.startsWith('image/'));
      const f = item?.getAsFile();
      if (f) { e.preventDefault(); choisirImage(f); }
    };
    window.addEventListener('paste', onPaste as any);
    return () => window.removeEventListener('paste', onPaste as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConvId, apercu]);

  const rafraichir = async (convId: number) => {
    await portailApi.personnelMessagesList(convId).then((r) => setMsgs(r.results || []));
    chargerConvs();
  };

  const envoyerAudio = async (blob: Blob, _dureeSec: number) => {
    if (!selectedConvId) return;
    const ext = blob.type.includes('ogg') ? 'ogg' : blob.type.includes('mp4') ? 'm4a' : 'webm';
    try {
      await portailApi.personnelEnvoyerFichier(selectedConvId, blob, `vocal_${Date.now()}.${ext}`);
      await rafraichir(selectedConvId);
    } catch (e: any) {
      alert(e?.message || "Erreur d'envoi du message vocal");
    }
  };

  const envoyer = async () => {
    if (!selectedConvId || envoi) return;
    if (image) {
      setEnvoi(true);
      try {
        await portailApi.personnelEnvoyerFichier(
          selectedConvId, image, image.name || `image_${Date.now()}.png`, texte.trim());
        setTexte('');
        choisirImage(null);
        await rafraichir(selectedConvId);
      } catch (e: any) {
        alert(e?.message || "Erreur d'envoi de l'image");
      } finally {
        setEnvoi(false);
      }
      return;
    }
    if (!texte.trim()) return;
    setEnvoi(true);
    try {
      await portailApi.personnelEnvoyerMessage(selectedConvId, texte.trim());
      setTexte('');
      // Recharger + rafraichir liste (bump activité)
      await portailApi.personnelMessagesList(selectedConvId).then((r) => setMsgs(r.results || []));
      chargerConvs();
    } catch (e: any) {
      alert(e?.message || 'Erreur d\'envoi');
    } finally {
      setEnvoi(false);
    }
  };

  const selectedConv = useMemo(
    () => convs.find(c => c.id === selectedConvId) || null,
    [convs, selectedConvId],
  );

  // ─── Vue liste (drawer principal) ─────────────────────────────────────
  if (!selectedConvId) {
    return (
      <div className={embedded ? 'space-y-2' : 'p-4 space-y-2'}>
        <div className="text-xs text-gray-500 px-1">
          Discussions avec la scolarité, les formateurs et l'administration.
          Ouvrez une conversation depuis le bouton flottant en bas à droite
          (onglet <span className="text-gray-300 font-medium">Personnel</span>).
        </div>
        {loadingList ? (
          <div className="flex items-center justify-center py-12 text-gray-500 text-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> Chargement…
          </div>
        ) : convs.length === 0 ? (
          <div className="text-center py-16 px-6">
            <Briefcase size={32} className="mx-auto text-gray-700 mb-3" />
            <div className="text-sm text-gray-400">
              Aucune conversation avec le personnel pour l'instant.
            </div>
            <div className="text-[11px] text-gray-600 mt-2 leading-relaxed">
              Ouvrez le bouton flottant en bas à droite,<br />
              onglet <span className="text-gray-400 font-medium">Personnel</span>,
              puis cliquez sur un agent, formateur ou responsable.
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            {convs.map(c => {
              // On identifie l'"autre" participant pour rôle badge
              const autre = c.participants_apercu.find(p => p.role_conv !== 'admin')
                || c.participants_apercu[0] || null;
              const derniereActivite = fmtDateCourt(c.date_derniere_activite);
              return (
                <button
                  key={c.id}
                  onClick={() => selectionner(c)}
                  className="w-full flex items-start gap-3 p-3 rounded-lg bg-gray-900 hover:bg-gray-800/80 border border-gray-800 hover:border-blue-700 text-left"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white text-xs font-bold">
                    {(c.titre_affichage || '?')
                      .split(/\s+/).slice(0, 2).map(s => s[0]).join('').toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-medium text-gray-100 truncate">
                        {c.titre_affichage}
                      </div>
                      {autre && (
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded border ${
                            ROLE_BADGE[autre.role_conv] || 'bg-gray-800 text-gray-400 border-gray-700'
                          }`}
                        >
                          Personnel
                        </span>
                      )}
                      <span className="ml-auto text-[10px] text-gray-500">
                        {derniereActivite}
                      </span>
                    </div>
                    <div className="text-[11px] text-gray-400 truncate mt-0.5">
                      {c.dernier_message
                        ? `${c.dernier_message.expediteur_nom}: ${c.dernier_message.contenu}`
                        : 'Nouvelle conversation'}
                    </div>
                  </div>
                  {c.nb_non_lus > 0 && (
                    <span className="flex-shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">
                      {c.nb_non_lus > 99 ? '99+' : c.nb_non_lus}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ─── Vue conversation ────────────────────────────────────────────────
  return (
    <div className={embedded ? 'flex flex-col h-[calc(100vh-180px)] min-h-[400px]' : 'flex flex-col h-screen'}>
      {/* Header */}
      <div className="px-3 py-2 border-b border-gray-800 bg-gray-900 flex items-center gap-2">
        <button
          onClick={retourListe}
          className="p-1.5 rounded hover:bg-gray-800 text-gray-300"
          aria-label="Retour à la liste"
        ><ArrowLeft size={16} /></button>
        <MessageSquare size={16} className="text-blue-400" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-gray-100 truncate">
            {selectedConv?.titre_affichage || 'Conversation'}
          </div>
          <div className="text-[10px] text-gray-500">Personnel administratif</div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-950">
        {loadingChat ? (
          <div className="flex items-center justify-center py-8 text-gray-500 text-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> Chargement…
          </div>
        ) : msgs.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            Aucun message pour l'instant. Écrivez le premier ci-dessous.
          </div>
        ) : (
          msgs.map(m => (
            <div
              key={m.id}
              className={`flex ${m.a_moi ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] px-3 py-2 rounded-2xl ${
                  m.a_moi
                    ? 'bg-blue-600 text-white rounded-br-sm'
                    : 'bg-gray-800 text-gray-100 rounded-bl-sm'
                }`}
              >
                {!m.a_moi && (
                  <div className="text-[10px] font-semibold text-blue-300 mb-0.5">
                    {m.expediteur_nom}
                  </div>
                )}
                {m.fichier_url && (m.fichier_mime?.startsWith('image/') || m.type_msg === 'image') && (
                  <img
                    src={m.fichier_url}
                    alt={m.fichier_nom || 'image'}
                    loading="lazy"
                    onClick={() => setLightbox({ url: m.fichier_url!, nom: m.fichier_nom })}
                    className="mb-1 max-h-60 max-w-full rounded-lg cursor-zoom-in object-contain bg-black/20"
                  />
                )}
                {m.fichier_url && m.fichier_mime?.startsWith('audio/') && (
                  <audio src={m.fichier_url} controls preload="metadata" className="mb-1 max-w-[240px] h-10" />
                )}
                {m.contenu && (
                  <div className="text-sm whitespace-pre-wrap break-words">
                    {m.contenu}
                  </div>
                )}
                {m.fichier_url && !m.fichier_mime?.startsWith('audio/')
                  && !(m.fichier_mime?.startsWith('image/') || m.type_msg === 'image') && (
                  <a
                    href={m.fichier_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block mt-1 text-[11px] underline ${m.a_moi ? 'text-blue-100' : 'text-blue-300'}`}
                  >
                    📎 {m.fichier_nom || 'Pièce jointe'}
                  </a>
                )}
                <div
                  className={`text-[10px] mt-1 text-right ${
                    m.a_moi ? 'text-blue-200/70' : 'text-gray-500'
                  }`}
                >
                  {fmtHeure(m.date_envoi)}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Composer — l'enregistreur vocal reste monté pour ne pas perdre l'enregistrement */}
      <div className="p-2 border-t border-gray-800 bg-gray-900">
        {apercu && (
          <div className="mb-2 flex items-center gap-2 bg-gray-800 rounded-lg p-2">
            <img src={apercu} alt="Image à envoyer" className="h-16 w-16 object-cover rounded" />
            <div className="flex-1 min-w-0 text-[11px] text-gray-400 truncate">
              {image?.name || 'Image collée'} — ajoutez un texte si besoin, puis envoyez.
            </div>
            <button onClick={() => choisirImage(null)} className="p-1 text-gray-400 hover:text-white" aria-label="Retirer l'image">
              <X size={14} />
            </button>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => { choisirImage(e.target.files?.[0] || null); e.target.value = ''; }}
        />
        <div className="flex items-end gap-2">
          {audioPhase === 'idle' && (
            <>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={envoi}
                className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-blue-400 hover:bg-gray-800 rounded-full disabled:opacity-50 flex-shrink-0"
                title="Envoyer une image (ou Ctrl+V)"
                aria-label="Envoyer une image"
              >
                <ImageIcon size={18} />
              </button>
              <div className="relative flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowEmoji((v) => !v)}
                  disabled={envoi}
                  className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-yellow-400 hover:bg-gray-800 rounded-full disabled:opacity-50"
                  title="Emojis"
                  aria-label="Emojis"
                >
                  <span className="text-lg leading-none">😊</span>
                </button>
                {showEmoji && (
                  <EmojiPicker
                    onPick={(e) => setTexte((t) => t + e)}
                    onClose={() => setShowEmoji(false)}
                  />
                )}
              </div>
              <textarea
                value={texte}
                onChange={(e) => setTexte(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault(); envoyer();
                  }
                }}
                placeholder="Écrire un message… (Entrée pour envoyer)"
                rows={1}
                className="flex-1 resize-none px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:border-blue-500 max-h-32"
              />
            </>
          )}
          {audioPhase === 'idle' && (texte.trim() || image) && (
            <button
              onClick={envoyer}
              disabled={envoi}
              className="p-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
              aria-label="Envoyer"
            >
              {envoi ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          )}
          <div className={`${audioPhase !== 'idle' ? 'flex-1' : ''} ${audioPhase === 'idle' && (texte.trim() || image) ? 'hidden' : ''}`}>
            <AudioRecorder
              onRecorded={envoyerAudio}
              onPhaseChange={setAudioPhase}
              disabled={envoi}
            />
          </div>
        </div>
      </div>
      {lightbox && (
        <PhotoLightbox url={lightbox.url} label={lightbox.nom} allowDownload onClose={() => setLightbox(null)} />
      )}
    </div>
  );
}
